import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Row, Col, Card, Statistic, Checkbox, Spin, Tooltip, Input, Button } from 'antd';
import { ArrowRightOutlined, DownOutlined, RightOutlined, EnvironmentOutlined, SearchOutlined, CaretRightOutlined, CaretDownOutlined } from '@ant-design/icons';
import { useRequest, useLocalStorageState } from 'ahooks';
import * as echarts from 'echarts';
import styled from 'styled-components';

import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import FacilityMap from './components/facility-map';
import SpiderChart from './components/SpiderChart';
import SelectMonthRange from './components/SelectMonthRange';
import FacilityChart from './components/facility-chart';
import FacilityStackedChart from './components/FacilityStackedChart';
import FacilityCostsStackedChart from './components/FacilityCostsStackedChart';
import FacilityEUIStackedChart from './components/FacilityEUIStackedChart';
import {
    getFacilitysByCompanyId,
    getPortfolioEnergyData,
    getPortfolioEuiTrend,
    getCompanyElectricityGasSummary,
    transformFacilitiesData,
    transformEnergyData,
    transformEuiTrendData,
    transformCompanySummaryData,
    generateChartDataFromFacilities,
    generateRadarDataFromFacilities,
    aggregateMonthlyData,
    aggregateEuiData
} from '@/services/company';
import { useSelector } from 'react-redux';
import appActions from '@/redux/app/actions';
import facilityActions from '@/redux/facility/actions';
import moment from 'moment';
import useSelectedFacilitiesStorageByCompanyId from '@/hooks/useSelectedFacilitiesStorageByCompanyId';


// 响应式容器组件
const ResponsiveContainer = styled.div`
    display: flex;
    gap: 16px;
    align-items: flex-start;
    flex-direction: row;
    
    @media (max-width: 1700px) {
        flex-direction: column;
    }
`;

// 响应式信息容器
const InfoContainer = styled.div`
    flex: 1;
    
    @media (max-width: 1700px) {
        flex: none;
    }
`;

// 响应式图表容器
const ChartContainer = styled.div`
    width: 220px;
    height: 200px;
    flex-shrink: 0;
    
    @media (max-width: 1700px) {
        width: 100%;
    }
`;

const HoverWrapperSpan = styled.span`
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0; /* 允许flex项目收缩到内容宽度以下 */
`;

const HoverSpan = styled.span`
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
`;

const LayoutContentWrapper = styled.div`
  padding: 20px;
  background: #f0f2f5;
  min-height: 100vh;
.ant-checkbox-inner, .ant-checkbox, .ant-checkbox-wrapper:hover .ant-checkbox-inner, .ant-checkbox:focus, .ant-checkbox:hover, .ant-checkbox:hover::after, .ant-checkbox-wrapper:hover .ant-checkbox::after {
    border-radius: 2px;
  }
`;

const StatsWrapper = styled.div`
  margin-bottom: 24px;
  
  .ant-card {
    text-align: center;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
`;

const ChartCard = styled(Card)`
  margin-bottom: 16px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  
  .ant-card-body {
    padding: 16px;
  }
`;

const RadarChartCard = styled(Card)`
  margin-bottom: 16px;
  border-radius: 8px;
  
  .ant-card-body {
    padding: 24px;
  }
`;

const FacilityCard = styled(Card)`
  margin-bottom: 4px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  
  ${props => props.$highlighted && `
    transform: scale(1.02);
    box-shadow: 0 4px 20px rgba(24, 144, 255, 0.3);
    border: 2px solid #1890ff;
    animation: pulse 1.5s ease-in-out infinite;
    
    @keyframes pulse {
      0% {
        box-shadow: 0 4px 20px rgba(24, 144, 255, 0.3);
      }
      50% {
        box-shadow: 0 4px 25px rgba(24, 144, 255, 0.5);
      }
      100% {
        box-shadow: 0 4px 20px rgba(24, 144, 255, 0.3);
      }
    }
  `}
  
  .ant-card-head {
    background: linear-gradient(45deg, ${props => props.$facilityColor || '#667eea'} 95%, #fff 100%);
    color: white;
    border-radius: 8px 8px 0 0;
    min-height: unset;
    padding: 6px 0;
    font-size: 14px;
    
    .ant-card-head-title {
      color: white;
      font-weight: 600;
      padding: 0 10px;
      font-size: 14px;
    }
  }


`;

const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
`;

const OperationBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  margin-bottom: 16px;
  gap: 16px;

  .left-section {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .middle-section {
    flex: 1;
    max-width: 300px;
  }

  .right-section {
    display: flex;
    align-items: center;
    gap: 8px;
  }
`;

const PortfolioManage = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const companyId = useSelector(state => state.Company.selectedId);
    const [collapsedFacilities, setCollapsedFacilities] = useState({});
    const [highlightedFacilityId, setHighlightedFacilityId] = useState(null); // 高亮的设施ID


    // 使用 useLocalStorageState 缓存选中的设施，按 companyId 分组存储
    const [selectedFacilities, setSelectedFacilities, hasCache] = useSelectedFacilitiesStorageByCompanyId(companyId);
    const [searchKeyword, setSearchKeyword] = useState(''); // 搜索关键词

    // 月份范围状态
    const [monthRangeState, setMonthRangeState] = useState({
        month: 12,
        ending_date: moment().subtract(1, 'month').format('YYYY-MM')
    });

    // 使用 useMemo 来稳定 monthRange 引用，避免无限循环
    const monthRange = useMemo(() => {
        return {
            month: monthRangeState.month,
            ending_date: monthRangeState.ending_date
        };
    }, [monthRangeState.month, monthRangeState.ending_date]);

    // 使用 useCallback 来稳定 onChange 函数引用
    const handleMonthRangeChange = useCallback((newRange) => {
        setMonthRangeState(newRange);
    }, []);

    const mapRef = useRef(); // 添加地图引用
    const facilityRefs = useRef({}); // 设施卡片的引用

    // 获取设施列表数据
    const {
        data: facilitiesData,
        loading: facilitiesLoading,
        error: facilitiesError
    } = useRequest(() => getFacilitysByCompanyId(companyId), {
        refreshDeps: [companyId],
        onSuccess: (res) => {
            console.log('设施数据获取成功:', res);

            // 检查当前公司是否有缓存数据
            if (!hasCache(companyId)) {
                // 如果没有缓存，转换数据并自动全选
                const transformedFacilities = transformFacilitiesData(res);
                if (transformedFacilities.length > 0) {
                    const allFacilityIds = new Set(transformedFacilities.map(f => f.id));
                    setSelectedFacilities(allFacilityIds);
                    console.log(`公司 ${companyId} 无缓存，自动全选 ${allFacilityIds.size} 个设施`);
                }
            } else {
                console.log(`公司 ${companyId} 有缓存，跳过自动全选`);
            }

            // 初始化所有设施为默认折叠状态
            const transformedFacilities = transformFacilitiesData(res);
            if (transformedFacilities.length > 0) {
                const initialCollapsed = {};
                transformedFacilities.forEach(f => {
                    initialCollapsed[f.id] = true; // true 表示折叠
                });
                setCollapsedFacilities(initialCollapsed);
                console.log(`初始化 ${transformedFacilities.length} 个设施为折叠状态`);
            }
        }
    });

    // 转换设施数据
    const facilities = useMemo(() => {
        return transformFacilitiesData(facilitiesData);
    }, [facilitiesData]);



    // 提取所有设施ID列表 - 用于API请求（获取所有数据）
    const allFacilityIds = useMemo(() => {
        return facilities.map(f => f.id);
    }, [facilities]);

    // 使用 useMemo 来稳定 allFacilityIds 的字符串表示，用于依赖项比较
    const allFacilityIdsString = useMemo(() => {
        return allFacilityIds.join(',');
    }, [allFacilityIds]);

    // 使用 useMemo 来稳定 monthRange 的字符串表示
    const monthRangeString = useMemo(() => {
        return `${monthRange.month}-${monthRange.ending_date}`;
    }, [monthRange.month, monthRange.ending_date]);

    // 根据选中状态筛选设施 - 用于前端筛选
    const selectedFacilitiesData = useMemo(() => {
        return facilities.filter(facility => selectedFacilities.has(facility.id));
    }, [facilities, selectedFacilities]);

    // 统一的请求 - 获取所有数据
    const {
        data: portfolioData,
        loading: portfolioLoading
    } = useRequest(
        async () => {
            const [energyData, euiTrendData, summaryData] = await Promise.all([
                getPortfolioEnergyData(allFacilityIds, monthRange),
                getPortfolioEuiTrend(allFacilityIds, monthRange),
                getCompanyElectricityGasSummary(companyId, monthRange)
            ]);

            return {
                energyData,
                euiTrendData,
                summaryData
            };
        },
        {
            ready: allFacilityIds.length > 0 && companyId, // 只有当设施ID和公司ID准备好时才发请求
            refreshDeps: [allFacilityIdsString, monthRangeString, companyId]
        }
    );

    // 解构数据
    const energyData = portfolioData?.energyData;
    const euiTrendData = portfolioData?.euiTrendData;
    const summaryData = portfolioData?.summaryData;
    const energyLoading = portfolioLoading;
    const euiTrendLoading = portfolioLoading;

    // 简化的loading逻辑 - 只在数据更新时显示loading，初始加载时不显示
    const showDataLoading = portfolioLoading && (energyData || euiTrendData || summaryData) && !facilitiesLoading;

    // 处理能源数据 - 先转换所有数据
    const processedEnergyData = useMemo(() => {
        return transformEnergyData(energyData);
    }, [energyData]);

    // 处理EUI趋势数据 - 先转换所有数据
    const processedEuiData = useMemo(() => {
        return transformEuiTrendData(euiTrendData);
    }, [euiTrendData]);

    // 处理汇总数据
    const processedSummaryData = useMemo(() => {
        return transformCompanySummaryData(summaryData);
    }, [summaryData]);

    // 根据选中设施筛选能源数据
    const filteredEnergyData = useMemo(() => {
        if (!processedEnergyData.electricityData || !processedEnergyData.gasData) {
            return { electricityData: [], gasData: [] };
        }

        const selectedFacilityIds = new Set(selectedFacilitiesData.map(f => String(f.id))); // 确保转换为字符串

        const filteredElectricity = processedEnergyData.electricityData.filter(item =>
            selectedFacilityIds.has(String(item.facility_id)) // 确保转换为字符串进行比较
        );

        const filteredGas = processedEnergyData.gasData.filter(item =>
            selectedFacilityIds.has(String(item.facility_id)) // 确保转换为字符串进行比较
        );

        return {
            electricityData: filteredElectricity,
            gasData: filteredGas
        };
    }, [processedEnergyData, selectedFacilitiesData]);

    // 根据选中设施筛选EUI数据
    const filteredEuiData = useMemo(() => {
        if (!processedEuiData.electricityEUIData || !processedEuiData.gasEUIData) {
            return { electricityEUIData: [], gasEUIData: [] };
        }

        const selectedFacilityIds = new Set(selectedFacilitiesData.map(f => String(f.id))); // 确保转换为字符串

        const filteredElectricityEUI = processedEuiData.electricityEUIData.filter(item =>
            selectedFacilityIds.has(String(item.facility_id)) // 确保转换为字符串进行比较
        );

        const filteredGasEUI = processedEuiData.gasEUIData.filter(item =>
            selectedFacilityIds.has(String(item.facility_id)) // 确保转换为字符串进行比较
        );

        return {
            electricityEUIData: filteredElectricityEUI,
            gasEUIData: filteredGasEUI
        };
    }, [processedEuiData, selectedFacilitiesData]);

    // 聚合数据用于图表显示 - 基于筛选后的数据
    const chartData = useMemo(() => {
        return {
            electricityData: aggregateMonthlyData(filteredEnergyData.electricityData, 'sum'),
            gasData: aggregateMonthlyData(filteredEnergyData.gasData, 'sum'),
            electricityCostsData: aggregateMonthlyData(filteredEnergyData.electricityData, 'sum').map(item => ({
                month: item.month,
                costs: item.costs,
                usage: item.usage || 0 // 确保包含usage字段用于价格计算
            })),
            gasCostsData: aggregateMonthlyData(filteredEnergyData.gasData, 'sum').map(item => ({
                month: item.month,
                costs: item.costs,
                usage: item.usage || 0 // 确保包含usage字段用于价格计算
            })),
            electricityEUIData: aggregateEuiData(filteredEuiData.electricityEUIData, 'average'),
            gasEUIData: aggregateEuiData(filteredEuiData.gasEUIData, 'average')
        };
    }, [filteredEnergyData, filteredEuiData]);

    // 生成设施图表数据 - 基于所有设施（不依赖选中状态）
    const facilityChartData = useMemo(() => {
        return generateChartDataFromFacilities(facilities);
    }, [facilities]);

    // 生成雷达图数据 - 基于选中的设施
    const radarData = useMemo(() => {
        const data = generateRadarDataFromFacilities(selectedFacilitiesData);
        return data;
    }, [selectedFacilitiesData]);

    // 过滤后的设施列表
    const filteredFacilities = useMemo(() => {
        if (!searchKeyword.trim()) {
            return facilities;
        }
        return facilities.filter(facility =>
            facility.name.toLowerCase().includes(searchKeyword.toLowerCase())
        );
    }, [facilities, searchKeyword]);

    // 全选状态计算
    const selectAllState = useMemo(() => {
        if (filteredFacilities.length === 0) return false;

        const selectedCount = filteredFacilities.filter(f => selectedFacilities.has(f.id)).length;
        if (selectedCount === 0) return false;
        if (selectedCount === filteredFacilities.length) return 'all';
        return 'indeterminate';
    }, [filteredFacilities, selectedFacilities]);

    // 计算是否所有设施都收起
    const allCollapsed = useMemo(() => {
        if (filteredFacilities.length === 0) return true;
        return filteredFacilities.every(f => collapsedFacilities[f.id] !== false);
    }, [filteredFacilities, collapsedFacilities]);

    // 切换设施卡片的折叠状态
    const toggleFacilityCollapse = (facilityId) => {
        setCollapsedFacilities(prev => {
            const newState = {
                ...prev,
                [facilityId]: !prev[facilityId]
            };
            return newState;
        });
    };

    // 地图定位功能
    const handleLocateFacility = (facility) => {
        if (mapRef.current && facility.latitude && facility.longitude) {
            mapRef.current.focusOnFacility(facility.id);
        }
    };

    // 处理地图设施点击
    const handleMapFacilityClick = (facilityData) => {
        console.log('地图设施被点击:', facilityData);
        setHighlightedFacilityId(facilityData.id);

        // 滚动到对应的设施卡片
        const facilityElement = facilityRefs.current[facilityData.id];
        if (facilityElement) {
            facilityElement.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
                inline: 'nearest'
            });
        }

        // 3秒后移除高亮
        setTimeout(() => {
            setHighlightedFacilityId(null);
        }, 3000);
    };

    // 处理设施卡片点击 - 跳转到facility Dashboard
    const handleFacilityCardClick = (facility) => {
        navigate("/switch-view?viewMode=0&companyId=" + facility.company_id + "&facilityId=" + facility.id);
    };

    // 全选/取消全选
    const handleSelectAll = () => {
        if (selectAllState === 'all') {
            // 取消全选
            const newSelected = new Set(selectedFacilities);
            filteredFacilities.forEach(f => newSelected.delete(f.id));
            setSelectedFacilities(newSelected);
        } else {
            // 全选
            const newSelected = new Set(selectedFacilities);
            filteredFacilities.forEach(f => newSelected.add(f.id));
            setSelectedFacilities(newSelected);
        }
    };

    // 单个设施选择
    const handleFacilitySelect = (facilityId, checked) => {
        const newSelected = new Set(selectedFacilities);
        if (checked) {
            newSelected.add(facilityId);
        } else {
            newSelected.delete(facilityId);
        }
        setSelectedFacilities(newSelected);
    };

    // 全部展开
    const handleExpandAll = () => {
        const newCollapsed = {};
        filteredFacilities.forEach(f => {
            newCollapsed[f.id] = false;
        });
        setCollapsedFacilities(prev => ({ ...prev, ...newCollapsed }));

        // 图表会在组件内部自动初始化
    };

    // 全部折叠
    const handleCollapseAll = () => {
        const newCollapsed = {};
        filteredFacilities.forEach(f => {
            newCollapsed[f.id] = true;
        });
        setCollapsedFacilities(prev => ({ ...prev, ...newCollapsed }));
    };

    // 切换展开/收起状态
    const handleToggleExpandCollapse = () => {
        if (allCollapsed) {
            // 当前所有都收起，执行展开操作
            handleExpandAll();
        } else {
            // 有展开的设施，执行收起操作
            handleCollapseAll();
        }
    };

    // 图表会在组件内部自动初始化，无需在这里手动初始化





    // 加载状态
    // 只在初始加载时显示全局loading，数据更新时不显示
    const isInitialLoading = facilitiesLoading || (!energyData && !euiTrendData && !summaryData && portfolioLoading);

    if (facilitiesError) {
        return (
            <LayoutContentWrapper>
                <div style={{ padding: '50px', textAlign: 'center' }}>
                    <p>Data loading failed, please try again later</p>
                </div>
            </LayoutContentWrapper>
        );
    }

    if (isInitialLoading) {
        return (
            <LayoutContentWrapper>
                <LoadingWrapper>
                    <Spin size="large" />
                </LoadingWrapper>
            </LayoutContentWrapper>
        );
    }

    // 在渲染地图时使用选中的设施数据
    return (
        <LayoutContentWrapper>
            <div style={{ margin: '16px' }}>
                {/* 主要布局：前2列和后1列 */}
                <Row gutter={16}>
                    {/* 前2列：地图、雷达图和图表区域 */}
                    <Col span={16}>
                        {/**月份选择 */}
                        <SelectMonthRange
                            month={monthRange.month}
                            ending_date={monthRange.ending_date}
                            onChange={handleMonthRangeChange}
                        />
                        {/* 雷达图 - 基于选中的设施 */}
                        <Row gutter={16} style={{ marginTop: '16px' }}>
                            <Col span={12}>
                                <div style={{
                                    width: '100%',
                                    aspectRatio: '16/9',
                                    position: 'relative',
                                    overflow: 'hidden',
                                    borderRadius: 10,
                                }}>
                                    {showDataLoading ? (
                                        <div style={{
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            right: 0,
                                            bottom: 0,
                                            background: 'rgba(255, 255, 255, 0.8)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            zIndex: 10
                                        }}>
                                            <Spin size="large" tip="Loading performance data..." />
                                        </div>
                                    ) : selectedFacilitiesData.length > 0 ? (
                                        <SpiderChart
                                            radarData={radarData}
                                            facilities={selectedFacilitiesData}
                                            title="Portfolio Performance Overview"
                                            style={{
                                                width: '100%',
                                                height: '100%'
                                            }}
                                        />
                                    ) : (
                                        <div style={{
                                            width: '100%',
                                            height: '100%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: '#999',
                                            fontSize: '16px',
                                            background: '#f9f9f9',
                                            border: '1px solid #e8e8e8',
                                            borderRadius: '8px'
                                        }}>
                                            Please select at least one facility to view the performance overview
                                        </div>
                                    )}
                                </div>

                                {/* 设施地图 - 只显示选中的设施 */}
                                <div style={{
                                    width: '100%',
                                    aspectRatio: '16/9',
                                    position: 'relative',
                                    overflow: 'hidden',
                                    borderRadius: 10,
                                    marginTop: '16px'
                                }}>
                                    {showDataLoading && (
                                        <div style={{
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            right: 0,
                                            bottom: 0,
                                            background: 'rgba(255, 255, 255, 0.8)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            zIndex: 10
                                        }}>
                                            <Spin size="large" tip="Updating map data..." />
                                        </div>
                                    )}
                                    <FacilityMap
                                        facilities={facilities}
                                        ref={mapRef}
                                        onFacilityClick={handleMapFacilityClick}
                                        style={{ width: '100%', height: '100%' }}
                                    />
                                </div>
                            </Col>
                            <Col span={12}>
                                {/* 能源使用量和成本图表 - 基于选中的设施 */}
                                <Row gutter={16}>
                                    <Col span={24}>
                                        <div style={{ aspectRatio: '16/9' }}>
                                            {showDataLoading ? (
                                                <LoadingWrapper>
                                                    <Spin size="large" tip="Loading electricity data..." />
                                                </LoadingWrapper>
                                            ) : selectedFacilitiesData.length > 0 ? (
                                                <FacilityStackedChart
                                                    data={filteredEnergyData.electricityData}
                                                    type="electricity"
                                                    style={{ width: '100%', height: '100%' }}
                                                    title={`Electricity Usage`}
                                                    facilities={selectedFacilitiesData}
                                                />
                                            ) : (
                                                <div style={{
                                                    width: '100%',
                                                    height: '100%',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    background: '#f9f9f9',
                                                    border: '1px solid #e8e8e8',
                                                    borderRadius: '8px',
                                                    color: '#999'
                                                }}>
                                                    No facilities selected for electricity data
                                                </div>
                                            )}
                                        </div>
                                    </Col>
                                    <Col span={24} style={{ marginTop: '16px' }}>
                                        <div style={{ aspectRatio: '16/9' }}>
                                            {showDataLoading ? (
                                                <LoadingWrapper>
                                                    <Spin size="large" tip="Loading gas data..." />
                                                </LoadingWrapper>
                                            ) : selectedFacilitiesData.length > 0 ? (
                                                <FacilityStackedChart
                                                    data={filteredEnergyData.gasData}
                                                    type="gas"
                                                    style={{ width: '100%', height: '100%' }}
                                                    title={`Gas Usage`}
                                                    facilities={selectedFacilitiesData}
                                                />
                                            ) : (
                                                <div style={{
                                                    width: '100%',
                                                    height: '100%',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    background: '#f9f9f9',
                                                    border: '1px solid #e8e8e8',
                                                    borderRadius: '8px',
                                                    color: '#999'
                                                }}>
                                                    No facilities selected for gas data
                                                </div>
                                            )}
                                        </div>
                                    </Col>
                                </Row>
                            </Col>
                        </Row>

                        {/* 成本图表 - 基于选中的设施 */}
                        <Row gutter={16} style={{ marginTop: '16px' }}>
                            <Col span={12}>
                                <div style={{ aspectRatio: '16/9' }}>
                                    {showDataLoading ? (
                                        <LoadingWrapper>
                                            <Spin size="large" tip="Loading electricity costs data..." />
                                        </LoadingWrapper>
                                    ) : selectedFacilitiesData.length > 0 ? (
                                        <FacilityCostsStackedChart
                                            data={filteredEnergyData.electricityData}
                                            type="electricity"
                                            style={{ width: '100%', height: '100%' }}
                                            title={`Electricity Costs`}
                                            facilities={selectedFacilitiesData}
                                        />
                                    ) : (
                                        <div style={{
                                            width: '100%',
                                            height: '100%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            background: '#f9f9f9',
                                            border: '1px solid #e8e8e8',
                                            borderRadius: '8px',
                                            color: '#999'
                                        }}>
                                            No facilities selected for electricity costs data
                                        </div>
                                    )}
                                </div>
                            </Col>
                            <Col span={12}>
                                <div style={{ aspectRatio: '16/9' }}>
                                    {showDataLoading ? (
                                        <LoadingWrapper>
                                            <Spin size="large" tip="Loading gas costs data..." />
                                        </LoadingWrapper>
                                    ) : selectedFacilitiesData.length > 0 ? (
                                        <FacilityCostsStackedChart
                                            data={filteredEnergyData.gasData}
                                            type="gas"
                                            style={{ width: '100%', height: '100%' }}
                                            title={`Gas Costs`}
                                            facilities={selectedFacilitiesData}
                                        />
                                    ) : (
                                        <div style={{
                                            width: '100%',
                                            height: '100%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            background: '#f9f9f9',
                                            border: '1px solid #e8e8e8',
                                            borderRadius: '8px',
                                            color: '#999'
                                        }}>
                                            No facilities selected for gas costs data
                                        </div>
                                    )}
                                </div>
                            </Col>
                        </Row>

                        {/* EUI趋势图表 - 基于选中的设施 */}
                        <Row gutter={16} style={{ marginTop: '16px' }}>
                            <Col span={12}>
                                <div style={{ aspectRatio: '16/9' }}>
                                    {showDataLoading ? (
                                        <LoadingWrapper>
                                            <Spin size="large" tip="Loading electricity EUI data..." />
                                        </LoadingWrapper>
                                    ) : selectedFacilitiesData.length > 0 ? (
                                        <FacilityEUIStackedChart
                                            data={filteredEuiData.electricityEUIData}
                                            type="electricity"
                                            style={{ width: '100%', height: '100%' }}
                                            title={`Electricity EUI Trend`}
                                            facilities={selectedFacilitiesData}
                                        />
                                    ) : (
                                        <div style={{
                                            width: '100%',
                                            height: '100%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            background: '#f9f9f9',
                                            border: '1px solid #e8e8e8',
                                            borderRadius: '8px',
                                            color: '#999'
                                        }}>
                                            No facilities selected for electricity EUI data
                                        </div>
                                    )}
                                </div>
                            </Col>
                            <Col span={12}>
                                <div style={{ aspectRatio: '16/9' }}>
                                    {showDataLoading ? (
                                        <LoadingWrapper>
                                            <Spin size="large" tip="Loading gas EUI data..." />
                                        </LoadingWrapper>
                                    ) : selectedFacilitiesData.length > 0 ? (
                                        <FacilityEUIStackedChart
                                            data={filteredEuiData.gasEUIData}
                                            type="gas"
                                            style={{ width: '100%', height: '100%' }}
                                            title={`Gas EUI Trend`}
                                            facilities={selectedFacilitiesData}
                                        />
                                    ) : (
                                        <div style={{
                                            width: '100%',
                                            height: '100%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            background: '#f9f9f9',
                                            border: '1px solid #e8e8e8',
                                            borderRadius: '8px',
                                            color: '#999'
                                        }}>
                                            No facilities selected for gas EUI data
                                        </div>
                                    )}
                                </div>
                            </Col>
                        </Row>

                    </Col>

                    {/* 后1列：设施列表 */}
                    <Col span={8}>
                        {/* 操作栏 */}
                        <OperationBar>
                            {/* 左侧：全选控制 */}
                            <div className="left-section">
                                <Checkbox
                                    checked={selectAllState === 'all'}
                                    indeterminate={selectAllState === 'indeterminate'}
                                    onChange={handleSelectAll}
                                >
                                    Select All ({selectedFacilities.size})
                                </Checkbox>
                            </div>

                            {/* 中间：搜索框 */}
                            <div className="middle-section">
                                <Input
                                    placeholder="Search facilities by name..."
                                    prefix={<SearchOutlined />}
                                    value={searchKeyword}
                                    onChange={(e) => setSearchKeyword(e.target.value)}
                                    allowClear
                                />
                            </div>

                            {/* 右侧：展开折叠控制 */}
                            <div className="right-section">
                                <Tooltip title={allCollapsed ? "Expand All" : "Collapse All"}>
                                    <div
                                        style={{
                                            cursor: 'pointer',
                                            padding: '4px',
                                            borderRadius: '4px',
                                            transition: 'background-color 0.2s',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}
                                        onClick={handleToggleExpandCollapse}
                                        onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(0,0,0,0.04)'}
                                        onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                                    >
                                        {allCollapsed ? <CaretDownOutlined style={{ fontSize: '16px', color: '#666' }} /> : <CaretRightOutlined style={{ fontSize: '16px', color: '#666' }} />}
                                    </div>
                                </Tooltip>
                            </div>
                        </OperationBar>

                        {/* 设施列表 */}
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                            {filteredFacilities.length === 0 ? (
                                <div style={{
                                    padding: '40px',
                                    textAlign: 'center',
                                    color: '#999',
                                    background: 'white',
                                    borderRadius: '8px',
                                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                                }}>
                                    {searchKeyword ? (
                                        <>
                                            <SearchOutlined style={{ fontSize: '24px', marginBottom: '8px' }} />
                                            <p>No facilities found matching "{searchKeyword}"</p>
                                            <Button
                                                type="link"
                                                onClick={() => setSearchKeyword('')}
                                            >
                                                Clear search
                                            </Button>
                                        </>
                                    ) : (
                                        <p>No facilities available</p>
                                    )}
                                </div>
                            ) : (
                                filteredFacilities.map(facility => {
                                    const isCollapsed = collapsedFacilities[facility.id] !== false; // 默认为折叠状态
                                    const isHighlighted = highlightedFacilityId === facility.id;

                                    return (
                                        <FacilityCard
                                            key={facility.id}
                                            ref={el => {
                                                if (el) {
                                                    facilityRefs.current[facility.id] = el;
                                                }
                                            }}
                                            $highlighted={isHighlighted}
                                            $facilityColor={facility.color}
                                            onClick={(e) => {
                                                // 检查点击的元素是否应该阻止折叠
                                                const target = e.target;
                                                const isCheckbox = target.closest('.ant-checkbox') || target.closest('.ant-checkbox-wrapper');
                                                const isTitle = target.closest('[data-facility-title]');
                                                const isLocationButton = target.closest('[data-location-button]');
                                                
                                                // 如果点击的是checkbox、标题或定位按钮，则不触发折叠
                                                if (isCheckbox || isTitle || isLocationButton) {
                                                    return;
                                                }
                                                
                                                // 否则触发折叠
                                                toggleFacilityCollapse(facility.id);
                                            }}
                                            title={
                                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, minWidth: 0 }}>
                                                        <Checkbox
                                                            checked={selectedFacilities.has(facility.id)}
                                                            onChange={(e) => handleFacilitySelect(facility.id, e.target.checked)}
                                                        />
                                                        <span
                                                            style={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: '8px',
                                                                cursor: 'pointer',
                                                                fontSize: '14px',
                                                                fontWeight: 500,
                                                                flex: 1,
                                                                minWidth: 0
                                                            }}
                                                            onMouseEnter={(e) => {
                                                                const arrow = e.currentTarget.querySelector('.facility-arrow');
                                                                if (arrow) arrow.style.transform = 'translateX(4px)';
                                                            }}
                                                            onMouseLeave={(e) => {
                                                                const arrow = e.currentTarget.querySelector('.facility-arrow');
                                                                if (arrow) arrow.style.transform = 'translateX(0)';
                                                            }}

                                                        >
                                                            <HoverWrapperSpan title={facility.name}>
                                                                <HoverSpan
                                                            data-facility-title

                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleFacilityCardClick(facility);
                                                                    }}
                                                                >{facility.name}
                                                                </HoverSpan>
                                                            </HoverWrapperSpan>
                                                        </span>
                                                    </div>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                        {/* 只有当设施有经纬度时才显示定位按钮 */}
                                                        {facility.latitude && facility.longitude && (
                                                            <Tooltip
                                                                title="Locate this facility on map"
                                                                placement="top"
                                                            >
                                                                <div data-location-button>
                                                                    <EnvironmentOutlined
                                                                        style={{
                                                                            color: 'white',
                                                                            fontSize: '16px',
                                                                            cursor: 'pointer',
                                                                            opacity: 0.9,
                                                                            transition: 'opacity 0.2s'
                                                                        }}
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            handleLocateFacility(facility);
                                                                        }}
                                                                        onMouseEnter={(e) => e.target.style.opacity = '1'}
                                                                        onMouseLeave={(e) => e.target.style.opacity = '0.9'}
                                                                    />
                                                                </div>
                                                            </Tooltip>
                                                        )}
                                                        <div
                                                            style={{
                                                                padding: '4px',
                                                                borderRadius: '4px',
                                                                transition: 'background-color 0.2s'
                                                            }}
                                                            onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(255,255,255,0.2)'}
                                                            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                                                        >
                                                            {isCollapsed ? <CaretRightOutlined style={{ color: 'white' }} /> : <CaretDownOutlined style={{ color: 'white' }} />}
                                                        </div>
                                                    </div>
                                                </div>
                                            }
                                            bodyStyle={isCollapsed ? { padding: 0, height: 0, overflow: 'hidden' } : {}}
                                        >
                                            {!isCollapsed && (
                                                <ResponsiveContainer>
                                                    {/* 设施信息 */}
                                                    <InfoContainer>
                                                        <div style={{ marginBottom: '8px' }}>
                                                            <span style={{ color: '#666', fontSize: '12px' }}>Company:</span>
                                                            <p style={{ margin: '2px 0', fontWeight: '500' }}>{facility.company_name}</p>
                                                        </div>

                                                        {/* 设施类别和面积 */}
                                                        <div style={{ marginBottom: '8px' }}>
                                                            <span style={{ color: '#666', fontSize: '12px' }}>Category & Area:</span>
                                                            <p style={{ margin: '2px 0', fontWeight: '500' }}>
                                                                {facility.category} | {facility.area?.toLocaleString()} sqrft
                                                            </p>
                                                        </div>

                                                        {/* 电力使用量和成本 */}
                                                        <div style={{ marginBottom: '8px' }}>
                                                            <span style={{ color: '#666', fontSize: '12px' }}>Total Electricity Usage:</span>
                                                            <p style={{ margin: '2px 0', fontWeight: '500' }}>
                                                                {processedSummaryData[facility.id]?.electricity_total_usage
                                                                    ? `${Math.round(processedSummaryData[facility.id].electricity_total_usage).toLocaleString()} kWh`
                                                                    : '-'
                                                                }
                                                            </p>
                                                        </div>
                                                        <div style={{ marginBottom: '8px' }}>
                                                            <span style={{ color: '#666', fontSize: '12px' }}>Total Electricity Cost:</span>
                                                            <p style={{ margin: '2px 0', fontWeight: '500' }}>
                                                                {processedSummaryData[facility.id]?.electricity_total_cost
                                                                    ? `$${Math.round(processedSummaryData[facility.id].electricity_total_cost).toLocaleString()}`
                                                                    : '-'
                                                                }
                                                            </p>
                                                        </div>

                                                        {/* 天然气使用量和成本 */}
                                                        <div style={{ marginBottom: '8px' }}>
                                                            <span style={{ color: '#666', fontSize: '12px' }}>Total Gas Usage:</span>
                                                            <p style={{ margin: '2px 0', fontWeight: '500' }}>
                                                                {processedSummaryData[facility.id]?.gas_total_usage
                                                                    ? `${Math.round(processedSummaryData[facility.id].gas_total_usage).toLocaleString()} Therms`
                                                                    : '-'
                                                                }
                                                            </p>
                                                        </div>
                                                        <div style={{ marginBottom: '8px' }}>
                                                            <span style={{ color: '#666', fontSize: '12px' }}>Total Gas Cost:</span>
                                                            <p style={{ margin: '2px 0', fontWeight: '500' }}>
                                                                {processedSummaryData[facility.id]?.gas_total_cost
                                                                    ? `$${Math.round(processedSummaryData[facility.id].gas_total_cost).toLocaleString()}`
                                                                    : '-'
                                                                }
                                                            </p>
                                                        </div>

                                                        {/* 总成本 */}
                                                        <div style={{ marginBottom: '8px' }}>
                                                            <span style={{ color: '#666', fontSize: '12px' }}>Total Cost:</span>
                                                            <p style={{ margin: '2px 0', fontWeight: '600', fontSize: '14px' }}>
                                                                {processedSummaryData[facility.id]?.total_cost
                                                                    ? `$${Math.round(processedSummaryData[facility.id].total_cost).toLocaleString()}`
                                                                    : '-'
                                                                }
                                                            </p>
                                                        </div>
                                                    </InfoContainer>

                                                    {/* 设施图表 */}
                                                    <ChartContainer>
                                                        <FacilityChart
                                                            facilityId={facility.id}
                                                            facilityChartData={facilityChartData}
                                                            facilityColor={facility.color}
                                                        />
                                                    </ChartContainer>
                                                </ResponsiveContainer>
                                            )}
                                        </FacilityCard>
                                    );
                                })
                            )}
                        </div>
                    </Col>
                </Row>
            </div>
        </LayoutContentWrapper>
    );
};

export default PortfolioManage;